import { createContext, useContext, useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";

type PendingChangesContextType = {
  hasChanges: boolean;
  setHasChanges: (v: boolean) => void;
  onSave?: () => void;
  onDiscard?: () => void;
  setHandlers: (h: { onSave: () => void; onDiscard: () => void }) => void;
};

const PendingChangesContext = createContext<PendingChangesContextType | null>(null);

export function PendingChangesProvider({ children }: { children: React.ReactNode }) {
  const [hasChanges, setHasChanges] = useState(false);
  const [handlers, setHandlers] = useState<{
    onSave: () => void;
    onDiscard: () => void;
  } | null>(null);

  return (
    <PendingChangesContext.Provider
      value={{
        hasChanges,
        setHasChanges,
        onSave: handlers?.onSave,
        onDiscard: handlers?.onDiscard,
        setHandlers,
      }}
    >
      {children}
    </PendingChangesContext.Provider>
  );
}

export function usePendingChanges() {
  const ctx = useContext(PendingChangesContext);
  if (!ctx) throw new Error("usePendingChanges must be used inside provider");
  return ctx;
}

export function useUnsavedChangesGuard(
  when: boolean,
  onDiscard: () => void
) {
  const blocker = useBlocker(when);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowModal(true);
    }
  }, [blocker.state]);

  const stay = () => {
    setShowModal(false);
    blocker.reset?.();
  };

  const discard = () => {
    setShowModal(false);
    onDiscard();
    blocker.proceed?.();
  };

  return {
    showModal,
    stay,
    discard,
  };
}